// Firebase web client.
// Note: the web SDK config is intentionally public — security is enforced by
// Firestore rules + Auth allowlists, not by hiding these keys.

import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  where,
} from "firebase/firestore";

// Read from CRA env vars (REACT_APP_* prefix). Defaults keep the existing
// project credentials so the app remains drop-in runnable.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyAKL6EArMTP2-zvW6kndWAAgNV1yzzGgRo",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "fullstack-organoomushroom.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "fullstack-organoomushroom",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "fullstack-organoomushroom.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "499471474070",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:499471474070:web:0f425d757346a882ba91b7",
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();

// Admin allowlist — only these accounts can access /admin.
// Add more emails here (or move to Firestore "admins" doc) as the team grows.
export const ADMIN_EMAILS = [
  "shubhambawane410@gmail.com",
  "organomushroom@gmail.com",
];

// --- Auth helpers ----------------------------------------------------------

export function loginWithEmail(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

export function loginWithGoogle() {
  return signInWithPopup(auth, googleProvider);
}

export function signOut() {
  return fbSignOut(auth);
}

export function isAdmin(user) {
  if (!user || !user.email) return false;
  return ADMIN_EMAILS.includes(user.email.toLowerCase());
}

// --- Firestore writes -------------------------------------------------------

export async function saveMessage(payload) {
  return addDoc(collection(db, "messages"), {
    ...payload,
    status: "new",
    createdAt: serverTimestamp(),
  });
}

export async function saveSubscriber(email, source = "footer") {
  return addDoc(collection(db, "subscribers"), {
    email: email.trim().toLowerCase(),
    source,
    createdAt: serverTimestamp(),
  });
}

export async function saveOrder(payload) {
  return addDoc(collection(db, "orders"), {
    ...payload,
    status: payload.status || "whatsapp_pending",
    createdAt: serverTimestamp(),
  });
}

// --- Firestore reads (admin) ----------------------------------------------

export async function listRecent(name, max = 50) {
  const q = query(collection(db, name), orderBy("createdAt", "desc"), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listNews(max = 100) {
  // Order by `date` (ISO string), then by createdAt as tiebreaker.
  const q = query(collection(db, "news"), orderBy("date", "desc"), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getNewsBySlug(slug) {
  const q = query(collection(db, "news"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function saveNews(article) {
  // Use slug as the document id for clean URLs and idempotent upsert.
  const id = article.slug;
  const ref = doc(db, "news", id);
  const existing = await getDoc(ref);
  const payload = {
    ...article,
    updatedAt: serverTimestamp(),
  };
  if (!existing.exists()) {
    payload.createdAt = serverTimestamp();
    await setDoc(ref, payload);
  } else {
    await updateDoc(ref, payload);
  }
  return id;
}

export async function deleteNews(id) {
  return deleteDoc(doc(db, "news", id));
}
